import type { nonPiiPost } from '@cecm-microservices-sdk/core/services/account/non-pii';
import type { infoPost } from '@cecm-microservices-sdk/core/services/device/info';
import type { validatePost } from '@cecm-microservices-sdk/core/services/device/validate';
import type { licensePlatePost } from '@cecm-microservices-sdk/core/services/identity/device/license-plate';
import type {
    MicroservicesCountryCode,
    submitUsedCarEligibilityRequest,
} from '@/services/microservices/usedCarEligibility';
import type { handleRflzResponse, RflzResponseHandlingResult } from './rflzResponseHandler';
import { resolveRflzEligibilityResult } from './rflzEligibilityResult';
import { LICENSE_PLATE_SUBMIT_MESSAGES } from './licensePlateSubmitMessages';

export interface LicensePlateEligibilityFormData {
    licensePlate: string;
    state: string;
    firstName: string;
    lastName: string;
    zipCode: string;
}

export interface LicensePlateEligibilityDependencies {
    client: Parameters<typeof licensePlatePost>[0];
    countryCode: MicroservicesCountryCode;
    licensePlatePost: typeof licensePlatePost;
    validatePost: typeof validatePost;
    nonPiiPost: typeof nonPiiPost;
    infoPost: typeof infoPost;
    submitUsedCarEligibilityRequest: typeof submitUsedCarEligibilityRequest;
    handleRflzResponse: typeof handleRflzResponse;
}

// Runs injected effects; production response handling reads browser location and emits analytics.
// The adapter supplies validated, trimmed fields and owns exception logging and UI updates.
export async function submitLicensePlateEligibility(
    { licensePlate, state, firstName, lastName, zipCode }: LicensePlateEligibilityFormData,
    deps: LicensePlateEligibilityDependencies,
): Promise<RflzResponseHandlingResult> {
    const { client, countryCode } = deps;
    const licensePlateResponse = await deps.licensePlatePost(client, { licensePlate, state });
    const vin = licensePlateResponse.data?.last4DigitsOfVin?.trim();

    if (licensePlateResponse.status !== 'SUCCESS' || !vin) {
        return {
            type: 'message',
            message: LICENSE_PLATE_SUBMIT_MESSAGES.vehicleLookupFailed,
        };
    }

    const deviceValidationResponse = await deps.validatePost(client, { vin, optInForNFL: false });
    const radioId = deviceValidationResponse.data?.last4DigitsOfRadioId?.trim();

    if (deviceValidationResponse.status !== 'SUCCESS' || !radioId) {
        return { type: 'message', message: LICENSE_PLATE_SUBMIT_MESSAGES.deviceValidationFailed };
    }

    // The account response is an eligibility gate; only its status is needed.
    const accountResponse = await deps.nonPiiPost(client, { radioId });

    if (accountResponse.status !== 'SUCCESS') {
        return {
            type: 'message',
            message: LICENSE_PLATE_SUBMIT_MESSAGES.accountLookupFailed,
        };
    }

    const deviceInfoResponse = await deps.infoPost(client, { radioId });
    const vehicleVin = deviceInfoResponse.data?.deviceInformation?.vehicle?.vin?.trim();

    if (deviceInfoResponse.status !== 'SUCCESS' || !vehicleVin) {
        return { type: 'message', message: LICENSE_PLATE_SUBMIT_MESSAGES.deviceInfoFailed };
    }

    const response = await deps.submitUsedCarEligibilityRequest(countryCode, {
        firstName,
        lastName,
        radioIdOrVIN: vehicleVin,
        zipCode,
    });

    return resolveRflzEligibilityResult(response, deps.handleRflzResponse(response, vehicleVin));
}
